import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { useAuth } from '../contexts/AuthContext';

const TrialBanner = () => {
  const { organization, profile, orgLoading } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  // Hide completely while org data is still loading to prevent flash
  if (orgLoading) return null;
  if (!organization) return null;

  // ✅ Super admins never see billing banners
  if (profile?.is_platform_admin) return null;

  const subscriptionStatus = organization?.subscription_status;
  const trialEndDate = organization?.trial_end_date;

  // ✅ 'trialing' = Stripe's status for free-month promo subscriptions → treat as active, no banner
  if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') return null;

  // Show for past_due
  if (subscriptionStatus === 'past_due') {
    return (
      <div className="w-full px-4 py-2.5 flex items-center justify-between text-sm z-30 bg-red-600 text-white">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <Icon name="AlertTriangle" size={15} className="flex-shrink-0" />
          <span className="truncate">
            Your payment is overdue — your account may be suspended soon.{' '}
            <button
              onClick={() => navigate('/billing')}
              className="underline font-semibold hover:no-underline ml-1"
            >
              Update payment method →
            </button>
          </span>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="ml-3 flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
    );
  }

  // Show for canceled
  if (subscriptionStatus === 'canceled') {
    return (
      <div className="w-full px-4 py-2.5 flex items-center justify-between text-sm z-30 bg-gray-700 text-white">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <Icon name="XCircle" size={15} className="flex-shrink-0" />
          <span className="truncate">
            Your subscription has been canceled.{' '}
            <button
              onClick={() => navigate('/billing')}
              className="underline font-semibold hover:no-underline ml-1"
            >
              Resubscribe →
            </button>
          </span>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="ml-3 flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
    );
  }

  // Only show trial banner for trial status
  if (subscriptionStatus !== 'trial') return null;
  if (!trialEndDate) return null;

  const now = new Date();
  const endDate = new Date(trialEndDate);
  const diffMs = endDate - now;
  const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  // Don't show if trial already expired (paywall handles that)
  if (daysRemaining <= 0) return null;

  const isUrgent = daysRemaining <= 3;
  const isWarning = daysRemaining <= 7;

  return (
    <div className={`w-full px-4 py-2.5 flex items-center justify-between text-sm z-30 ${
      isUrgent
        ? 'bg-red-600 text-white'
        : isWarning
        ? 'bg-amber-500 text-white' :'bg-primary text-primary-foreground'
    }`}>
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <Icon name={isUrgent ? 'AlertTriangle' : 'Clock'} size={15} className="flex-shrink-0" />
        <span className="truncate">
          {daysRemaining === 1
            ? 'Your free trial expires tomorrow!'
            : `Your free trial expires in ${daysRemaining} days.`}
          {' '}
          <button
            onClick={() => navigate('/billing')}
            className="underline font-semibold hover:no-underline ml-1"
          >
            Add payment method →
          </button>
        </span>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="ml-3 flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <Icon name="X" size={14} />
      </button>
    </div>
  );
};

export default TrialBanner;
